import { useMemo, useState } from "react";
import {
  useCreate,
  useDataProvider,
  useGetList,
  useGetMany,
  useNotify,
  useRefresh,
} from "ra-core";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ArrowRightLeft,
  Calculator,
  Check,
  CheckCircle2,
  Plus,
  Search,
} from "lucide-react";
import { PageHeader } from "../common/PageHeader";
import type { CrmDataProvider } from "../../providers/types";

type CreditNoteRow = {
  id: number;
  reference: string;
  invoice_id: number | null;
  customer_id: number | null;
  note_date: string;
  reason: string | null;
  base_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  total_amount: number;
  status: "draft" | "posted" | "reconciled" | "cancelled";
};

type InvoiceLookup = {
  id: number;
  reference: string;
  company_id: number | null;
  customer_name: string | null;
  customer_state: string | null;
  total_amount: number;
};

const fmtINR = (n: number | null | undefined) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label className={`block text-sm font-medium leading-none ${className ?? ""}`}>
    {children}
  </label>
);

export const CreditNotesPage = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [targetRef, setTargetRef] = useState("");
  const [matched, setMatched] = useState<InvoiceLookup | null>(null);
  const [creditAmt, setCreditAmt] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [posting, setPosting] = useState<number | null>(null);

  const { data, isLoading } = useGetList<CreditNoteRow>("credit_notes", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "note_date", order: "DESC" },
  });

  const ledger = useMemo(() => data ?? [], [data]);

  const companyIds = useMemo(
    () => Array.from(new Set(ledger.map((r) => r.customer_id).filter((id): id is number => id != null))),
    [ledger],
  );
  const { data: companies } = useGetMany<{ id: number; name: string }>(
    "companies",
    { ids: companyIds },
    { enabled: companyIds.length > 0 },
  );
  const companyNameById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const c of companies ?? []) map[c.id] = c.name;
    return map;
  }, [companies]);

  const resetForm = () => {
    setTargetRef("");
    setMatched(null);
    setCreditAmt("");
    setReason("");
    setPosted(false);
  };

  const openSheet = () => {
    resetForm();
    setSheetOpen(true);
  };

  const lookupInvoice = async () => {
    if (!targetRef.trim()) {
      notify("Enter an invoice reference.", { type: "warning" });
      return;
    }
    try {
      const res = await dataProvider.getList("customer_invoices", {
        pagination: { page: 1, perPage: 5 },
        sort: { field: "id", order: "DESC" },
        filter: { "reference@ilike": `%${targetRef.trim()}%` },
      });
      const inv = (res.data?.[0] as InvoiceLookup | undefined) ?? null;
      if (!inv) {
        notify(`No invoice found with reference ${targetRef}.`, {
          type: "warning",
        });
        return;
      }
      setMatched(inv);
    } catch (error) {
      notify(`Lookup failed: ${(error as Error).message}`, { type: "error" });
    }
  };

  const submitDraft = async () => {
    if (!matched || !creditAmt) return;
    const base = parseFloat(creditAmt);
    if (Number.isNaN(base) || base <= 0) return;
    const intraState = matched.customer_state
      ? matched.customer_state.toLowerCase() === "maharashtra"
      : false;
    const taxAmount = +(base * 0.18).toFixed(2);
    const total = +(base + taxAmount).toFixed(2);
    setSubmitting(true);
    try {
      await create(
        "credit_notes",
        {
          data: {
            invoice_id: matched.id,
            customer_id: matched.company_id,
            note_date: new Date().toISOString().slice(0, 10),
            reason,
            base_amount: base,
            cgst_amount: intraState ? +(taxAmount / 2).toFixed(2) : 0,
            sgst_amount: intraState ? +(taxAmount / 2).toFixed(2) : 0,
            igst_amount: intraState ? 0 : taxAmount,
            total_amount: total,
            status: "draft",
          },
        },
        { returnPromise: true },
      );
      setPosted(true);
      refresh();
      setTimeout(() => {
        setSheetOpen(false);
        resetForm();
      }, 1200);
    } catch (error) {
      notify(`Failed: ${(error as Error).message}`, { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const postDraft = async (cn: CreditNoteRow) => {
    setPosting(cn.id);
    try {
      await dataProvider.postCreditNote(cn.id);
      notify("Credit note posted.", { type: "success" });
      refresh();
    } catch (error) {
      notify(`Post failed: ${(error as Error).message}`, { type: "error" });
    } finally {
      setPosting(null);
    }
  };

  const calcTax = creditAmt
    ? (parseFloat(creditAmt) * 0.18).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })
    : "0.00";
  const calcTotal = creditAmt
    ? (parseFloat(creditAmt) * 1.18).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })
    : "0.00";

  return (
    <div className="p-6 bg-slate-50/30 min-h-screen">
      <PageHeader
        title="Credit Notes & Returns"
        description="Link credit notes to original invoices and auto-compute GST reversals."
      />

      <Card className="shadow-none border border-slate-200 bg-white overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50 py-4 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-900">
            <Calculator className="h-4 w-4 text-slate-500" />
            Credit Register Ledger
          </CardTitle>
          <Button
            onClick={openSheet}
            className="bg-emerald-600 hover:bg-emerald-700 font-bold text-white shadow-md text-xs h-8 gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" /> Issue Credit Note
          </Button>
        </CardHeader>

        <div className="w-full overflow-x-auto border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Note Ref
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Invoice
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Company
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Posting Date
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Base Credit
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  GST Reversal
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 text-right">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    Loading credit notes…
                  </td>
                </tr>
              )}
              {!isLoading && ledger.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    No credit notes yet. Click Issue Credit Note to start.
                  </td>
                </tr>
              )}
              {ledger.map((row) => {
                const gst =
                  Number(row.cgst_amount ?? 0) +
                  Number(row.sgst_amount ?? 0) +
                  Number(row.igst_amount ?? 0);
                return (
                  <tr
                    key={row.id}
                    className="even:bg-slate-50/50 hover:bg-slate-100/50 border-b border-slate-200 transition-colors"
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-bold text-primary align-middle">
                      {row.reference}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-mono font-bold text-slate-600 align-middle">
                      #{row.invoice_id ?? "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-semibold text-slate-800 align-middle">
                      {row.customer_id != null ? (companyNameById[row.customer_id] ?? "—") : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-slate-500 font-medium align-middle">
                      {fmtDate(row.note_date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-black text-slate-900 align-middle">
                      {fmtINR(row.base_amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-emerald-600 font-bold align-middle">
                      {fmtINR(gst)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      <div className="flex justify-end items-center gap-2">
                        <Badge
                          className={`
                            ${row.status === "posted" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : ""}
                            ${row.status === "reconciled" ? "bg-slate-100 text-slate-700 border-slate-200" : ""}
                            ${row.status === "draft" ? "bg-amber-100 text-amber-800 border-amber-200" : ""}
                            ${row.status === "cancelled" ? "bg-rose-100 text-rose-700 border-rose-200" : ""}
                            font-bold border shadow-none uppercase
                          `}
                        >
                          {row.status}
                        </Badge>
                        {row.status === "draft" && (
                          <Button
                            size="sm"
                            disabled={posting === row.id}
                            onClick={() => postDraft(row)}
                            className="h-8 text-xs font-bold"
                          >
                            {posting === row.id ? "Posting…" : "Post"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-[480px] p-0 bg-white flex flex-col h-full border-l border-slate-200">
          <SheetHeader className="p-6 bg-slate-900 text-white">
            <SheetTitle className="text-white text-xl font-bold flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-emerald-400" /> Reconcile Credit
              Reversal
            </SheetTitle>
            <SheetDescription className="text-slate-400 font-medium mt-1">
              Locate original invoice and enter credit allocation.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {!posted ? (
              <>
                <div>
                  <Label className="text-slate-500 font-bold text-xs uppercase tracking-wider mb-2 block">
                    Invoice Reference Lookup
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g. INV/2026-27/0001"
                      value={targetRef}
                      onChange={(e) => setTargetRef(e.target.value)}
                      className="h-11 border-slate-300 shadow-sm font-mono font-bold"
                    />
                    <Button
                      onClick={lookupInvoice}
                      className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-4 h-11 shadow-md transition-all"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {matched && (
                  <div className="animate-in slide-in-from-bottom-4 duration-300 space-y-6">
                    <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex gap-3 shadow-sm">
                      <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-[10px] font-extrabold text-emerald-800 uppercase tracking-widest mb-0.5">
                          Invoice located
                        </p>
                        <h4 className="text-base font-bold text-slate-900">
                          {matched.customer_name ?? "(customer)"}
                        </h4>
                        <p className="text-xs font-bold text-emerald-700 mt-0.5">
                          Face value: {fmtINR(matched.total_amount)}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600 font-bold text-sm">
                        Credit value (taxable base)
                      </Label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">
                          ₹
                        </span>
                        <Input
                          type="number"
                          value={creditAmt}
                          onChange={(e) => setCreditAmt(e.target.value)}
                          className="pl-9 h-12 border-slate-300 text-2xl font-black tracking-tight focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-600 font-bold text-sm">
                        Reason
                      </Label>
                      <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Short-shipment / pricing correction / etc."
                      />
                    </div>

                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 space-y-3 shadow-inner">
                      <div className="flex justify-between text-sm text-slate-600">
                        <span className="font-medium">
                          GST reversal (18%, split by customer state):
                        </span>
                        <span className="font-bold text-emerald-600">₹{calcTax}</span>
                      </div>
                      <hr className="border-slate-200 border-dashed" />
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-bold text-slate-900">
                          Total credit:
                        </span>
                        <span className="text-xl font-black text-slate-900">
                          ₹{calcTotal}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="h-full flex flex-col items-center justify-center space-y-4 animate-in zoom-in-95">
                <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center shadow-lg">
                  <Check className="h-12 w-12 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Draft saved</h3>
                <p className="text-slate-500 font-medium text-center">
                  Use the Post action in the ledger when you're ready to commit.
                </p>
              </div>
            )}
          </div>

          {!posted && matched && (
            <SheetFooter className="p-6 bg-slate-50 border-t">
              <Button
                onClick={submitDraft}
                disabled={!creditAmt || submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 text-lg gap-2 shadow-md shadow-emerald-200"
              >
                {submitting ? "Saving…" : "Save Draft"}
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
